import { inject, ChangeDetectionStrategy, Component, effect, input, OnInit, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { TableModule } from 'primeng/table';
import { CardContent } from '@/shared/components/card-content/card-content';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { FuncoesFeatureService, MedicoComposicaoResponse } from './funcoes.service';
import { FuncaoRequest } from '@/core/api/sicom-api/registro/medico/funcao/funcao.model';
import { catchError, finalize } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { Pageable } from '@/shared/models/pageable.model';
import { PrimeTableSort, compareChangedTableOrder, fromExternalComponentToApiSort } from '@/shared/models/sort.model';

interface FuncaoMandato {
    cargo: string;
    entidade: string;
    uf: string;
    inicioMandato: string;
    terminoMandato: string;
}

interface DetalheFuncao {
    nomePrestador: string;
    crm: string;
    uf: string;
    cnpj: string;
    local: string | { value: string; color?: string };
    atribuicao: string;
    dataInicial: string;
    dataFinal: string;
}

@Component({
    selector: 'app-funcoes',
    standalone: true,
    imports: [TableModule, CardContent, CustomTableComponent],
    templateUrl: './funcoes.html',
    styleUrl: './funcoes.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Aba de funções e composição (mandatos) do médico. */
export class Funcoes implements OnInit {
    private readonly logger = inject(LoggerService);
    pessoa = input<Pessoa>();

    funcoesMandatos = signal<FuncaoMandato[]>([]);
    funcoesTableMetadata = {
        headers: {
            cargo: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'Cargo'
            },
            entidade: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'Entidade'
            },
            uf: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'UF'
            },
            inicioMandato: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'Início Mandato',
                orderBy: { asc: true }
            },
            terminoMandato: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'Término Mandato',
                orderBy: { asc: true }
            }
        },
        title: 'cargo',
        tag: 'uf',
        defaultSortField: 'inicioMandato',
        defaultSortOrder: 1
    } satisfies TableMetadata;

    // Tabela inferior (detalhamento por CRM)
    detalhesFuncoes = signal<DetalheFuncao[]>([]);
    detalhesFuncoesTableMetadata = {
        headers: {
            nomePrestador: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'Nome Prestador',
                orderBy: { asc: true }
            },
            crm: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'CRM'
            },
            uf: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'UF'
            },
            cnpj: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'CNPJ'
            },
            local: {
                type: 'tag',
                default: '',
                hidden: false,
                value: 'Local'
            },
            atribuicao: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'Atribuição'
            },
            dataInicial: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'Data Inicial',
                orderBy: { asc: true }
            },
            dataFinal: {
                type: 'text',
                default: '',
                hidden: false,
                value: 'Data Final',
                orderBy: { asc: true }
            }
        },
        title: 'nomePrestador',
        defaultSortField: 'dataInicial',
        defaultSortOrder: 1,
        tag: 'crm'
    } satisfies TableMetadata;

    lastComposicaoOrder?: { field: string; order: 1 | -1 };
    lastDetalheOrder?: { field: string; order: 1 | -1 };

    isLoading = signal(false);
    erroFuncoes = signal<string | undefined>(undefined);

    constructor(private funcoesService: FuncoesFeatureService) {
        effect(() => {
            const p = this.pessoa();
            if (p && p.id !== undefined && p.id !== null && !!p.uf) {
                this.lastComposicaoOrder = undefined;
                this.lastDetalheOrder = undefined;
                this.carregarFuncoes(p);
            } else {
                this.lastComposicaoOrder = undefined;
                this.lastDetalheOrder = undefined;
                this.resetarTabelas();
            }
        });
    }

    ngOnInit(): void {}

    private carregarFuncoes(pessoa: Pessoa, pageable?: Pageable): void {
        const params: Pessoa = { id: pessoa.id, uf: pessoa.uf };
        this.isLoading.set(true);
        this.erroFuncoes.set(undefined);
        const funcoes$ = this.funcoesService.buscar(params, pageable).pipe(
            catchError((err) => {
                this.logger.error('Erro ao buscar funções do médico:', err);
                this.erroFuncoes.set('Falha ao carregar funções do médico.');
                return of([] as FuncaoRequest[]);
            })
        );

        const composicao$ = this.funcoesService.buscarComposicao(params).pipe(
            catchError((err) => {
                this.logger.error('Erro ao buscar composição do médico:', err);
                return of([] as MedicoComposicaoResponse[]);
            })
        );

        forkJoin([funcoes$, composicao$])
            .pipe(finalize(() => this.isLoading.set(false)))
            .subscribe(([funcoesResp, composicaoResp]) => {
                if (funcoesResp && Array.isArray(funcoesResp)) {
                    this.aplicarFuncao(funcoesResp);
                } else {
                    this.detalhesFuncoes.set([]);
                }

                if (composicaoResp && Array.isArray(composicaoResp)) {
                    this.aplicarComposicao(composicaoResp);
                } else {
                    this.funcoesMandatos.set([]);
                }

                if ((!funcoesResp || !funcoesResp.length) && (!composicaoResp || !composicaoResp.length)) {
                    this.resetarTabelas();
                }
            });
    }

    private carregarFuncoesOrdenadas(pessoa: Pessoa, pageable: Pageable): void {
        const params: Pessoa = { id: pessoa.id, uf: pessoa.uf };
        this.isLoading.set(true);
        this.erroFuncoes.set(undefined);

        this.funcoesService
            .buscar(params, pageable)
            .pipe(
                catchError((err) => {
                    this.logger.error('Erro ao buscar funções do médico:', err);
                    this.erroFuncoes.set('Falha ao carregar funções do médico.');
                    return of([] as FuncaoRequest[]);
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe((funcoesResp) => {
                if (funcoesResp && Array.isArray(funcoesResp)) {
                    this.aplicarFuncao(funcoesResp);
                } else {
                    this.detalhesFuncoes.set([]);
                }
            });
    }

    private carregarComposicaoOrdenada(pessoa: Pessoa, pageable: Pageable): void {
        const params: Pessoa = { id: pessoa.id, uf: pessoa.uf };
        this.isLoading.set(true);

        this.funcoesService
            .buscarComposicao(params, pageable)
            .pipe(
                catchError((err) => {
                    this.logger.error('Erro ao buscar composição do médico:', err);
                    return of([] as MedicoComposicaoResponse[]);
                }),
                finalize(() => this.isLoading.set(false))
            )
            .subscribe((composicaoResp) => {
                if (composicaoResp && Array.isArray(composicaoResp)) {
                    this.aplicarComposicao(composicaoResp);
                } else {
                    this.funcoesMandatos.set([]);
                }
            });
    }

    private aplicarFuncao(lista: FuncaoRequest[]): void {
        this.detalhesFuncoes.set(
            lista.map((f) => ({
                nomePrestador: f?.nmPrestador ?? '',
                crm: f?.nuCrmPrestador ?? '',
                uf: f?.ufPrestador ?? '',
                cnpj: f?.nuCpfCgc ?? '',
                local: this.mapearLocalBadge(f?.local),
                atribuicao: f?.tipo ?? '',
                dataInicial: this.formatarData(f?.dtInicio ?? ''),
                dataFinal: this.formatarData(f?.dtFim ?? null)
            }))
        );
    }

    private mapearLocalBadge(local?: string | null): { value: string; color?: string } {
        const normalizado = (local ?? '')
            .toString()
            .trim()
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

        if (normalizado.includes('CORPO') || normalizado.includes('CLINICO')) {
            return { value: 'CORPO CLÍNICO', color: '#60a5fa' };
        }

        if (normalizado.includes('DIRETOR')) {
            return { value: 'DIRETOR', color: '#fb923c' };
        }

        return { value: local?.toString().trim() || '-', color: '#9ca3af' };
    }

    private aplicarComposicao(lista: MedicoComposicaoResponse[]): void {
        this.funcoesMandatos.set(
            lista.map((c) => ({
                cargo: c?.dsCargo ?? '',
                entidade: c?.nmSolicitante ?? '',
                uf: c?.ufSgUf ?? '',
                inicioMandato: this.formatarData(c?.dtInicio ?? null),
                terminoMandato: this.formatarData(c?.dtFim ?? null)
            }))
        );
    }

    private resetarTabelas(): void {
        this.funcoesMandatos.set([]);
        this.detalhesFuncoes.set([]);
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return String(valor);
        }
    }

    onSortComposicao({ field, order }: PrimeTableSort) {
        if (!field || !order) return;
        if (this.lastComposicaoOrder && !this.changedTableOrder({ field, order }, this.lastComposicaoOrder)) return;
        const pageable = {
            sort: fromExternalComponentToApiSort({ field: this.mapSortFieldToApiComposicao(field), order })
        } satisfies Pageable;

        const p = this.pessoa();
        if (p && p.id !== undefined && p.id !== null && !!p.uf) {
            this.carregarComposicaoOrdenada(p, pageable);
        }

        this.lastComposicaoOrder = { field, order };
    }

    onSort({ field, order }: PrimeTableSort) {
        if (!field || !order) return;
        if (this.lastDetalheOrder && !this.changedTableOrder({ field, order }, this.lastDetalheOrder)) return;
        const pageable = {
            sort: fromExternalComponentToApiSort({ field: this.mapSortFieldToApi(field), order })
        } satisfies Pageable;

        const p = this.pessoa();
        if (p && p.id !== undefined && p.id !== null && !!p.uf) {
            this.carregarFuncoesOrdenadas(p, pageable);
        }

        this.lastDetalheOrder = { field, order };
    }

    private mapSortFieldToApiComposicao(field: string): string {
        switch (field) {
            case 'cargo':
                return 'dsCargo';
            case 'entidade':
                return 'nmSolicitante';
            case 'uf':
                return 'ufSgUf';
            case 'inicioMandato':
                return 'dtInicio';
            case 'terminoMandato':
                return 'dtFim';
            default:
                return field;
        }
    }

    private mapSortFieldToApi(field: string): string {
        switch (field) {
            case 'nomePrestador':
                return 'nmPrestador';
            case 'dataInicial':
                return 'dtInicio';
            case 'dataFinal':
                return 'dtFim';
            default:
                return field;
        }
    }

    private changedTableOrder({ field, order }: PrimeTableSort, lastOrder: PrimeTableSort): boolean {
        let changedPage = fromExternalComponentToApiSort({ field, order });
        let lastPage = fromExternalComponentToApiSort(lastOrder);
        if (lastOrder) return compareChangedTableOrder(lastPage, changedPage);
        else return false;
    }
}
